/**
 * useSeo — a lightweight hook that sets document.title, meta description,
 * and canonical link tag per route. No react-helmet dependency needed.
 *
 * Usage:
 *   useSeo({
 *     title: "FAQ — GC Nexon",
 *     description: "Answers to the most common questions...",
 *     path: "/faq",             // canonical path relative to origin
 *     noindex: false,           // optional
 *   });
 */
import { useEffect } from "react";

const ORIGIN = "https://gcnexon.com";

function upsertMetaByName(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertMetaByProperty(prop, content) {
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function useSeo({ title, description, path, noindex = false, image }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) upsertMetaByName("description", description);
    const canonical = path ? `${ORIGIN}${path}` : ORIGIN + window.location.pathname;
    upsertCanonical(canonical);
    upsertMetaByName("robots", noindex ? "noindex,nofollow" : "index,follow");
    if (title) upsertMetaByProperty("og:title", title);
    if (description) upsertMetaByProperty("og:description", description);
    upsertMetaByProperty("og:url", canonical);
    upsertMetaByProperty("og:type", "website");
    if (image) upsertMetaByProperty("og:image", image);
    upsertMetaByName("twitter:card", "summary_large_image");
    if (title) upsertMetaByName("twitter:title", title);
    if (description) upsertMetaByName("twitter:description", description);
  }, [title, description, path, noindex, image]);
}
